import { useEffect, useState } from "react";
import api from "@/lib/api";
import { loadDemoConfig } from "@/components/demo/DemoCta";
import { fmtDay } from "@/components/demo/DemoSlotPicker";
import { ExistingDemoNotice, MoveDemoDialog, useExistingBooking } from "@/components/demo/MoveDemoDialog";
import { useFormAnalytics } from "@/lib/formAnalytics";
import { useDemoAutosave } from "@/lib/demoAutosave";
import { getConsent, utmParams } from "@/lib/analytics";
import { FIELD_ORDER, FieldError, fieldErrorsFrom, inputCls } from "@/components/demo/demoFieldErrors";

// Campaign tags for the lead, only when the visitor accepted analytics.
function attribution() {
  if (getConsent() !== "granted") return undefined;
  const u = utmParams();
  const a = { utm_source: u.campaign_source, utm_medium: u.campaign_medium, utm_campaign: u.campaign_name };
  Object.keys(a).forEach((k) => { if (!a[k]) delete a[k]; });
  return Object.keys(a).length ? a : undefined;
}

const FIELDS = [
  ["business_name", "Business name", "text", true],
  ["contact_name", "Your name", "text", true],
  ["work_email", "Work email", "email", true],
  ["job_title", "Job title", "text", true],
  ["website", "Business website", "text", true, "yourbrand.com"],
  ["industry", "Industry", "text", true, "e.g. Apparel, SaaS, Home goods"],
];

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace(/^Value error, /, "")).join(" ");
  return (typeof d === "string" ? d : d?.message) || "Something went wrong. Please try again.";
};

function Field({ name, label, type, required, placeholder, value, onChange, error }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-800">{label}{required ? " *" : " (optional)"}</span>
      <input name={name} type={type} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)} data-testid={`demo-field-${name}`}
        aria-invalid={!!error} aria-describedby={error ? `demo-error-${name}` : undefined} className={inputCls(error)} />
      <FieldError name={name} error={error} />
    </label>
  );
}

// Step 2 of the demo page: saves the request once, then books the time picked above (retries reuse the saved request).
export function DemoRequestForm({ slot, onBook }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ demo_interests: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [token, setToken] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [kept, setKept] = useState(false);
  useEffect(() => { loadDemoConfig().then(setCfg); }, []);
  const fa = useFormAnalytics("demo_request", { enabled: !!cfg?.enabled });
  const autosave = useDemoAutosave(form, setForm, !!cfg?.enabled);
  const [existing, setExisting] = useExistingBooking(form.work_email, form.website, !!cfg?.enabled);
  useEffect(() => { setToken(null); setKept(false); }, [form.work_email, form.website]);  // saved request is per email + website
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
  };
  const toggle = (i) => set("demo_interests", form.demo_interests.includes(i)
    ? form.demo_interests.filter((x) => x !== i) : [...form.demo_interests, i]);

  const submit = (e) => {
    e.preventDefault();
    if (!slot) { setError("Please choose a demo time above first."); return; }
    if (existing) { setConfirmOpen(true); return; }
    run(false);
  };

  const run = async (replace) => {
    setConfirmOpen(false); setKept(false); setBusy(true); setError(null);
    let t = token;
    try {
      if (!t) {
        const body = { ...form };
        ["company_size", "province", "message"].forEach((k) => { if (!body[k]) delete body[k]; });
        const attr = attribution();
        if (attr) body.attribution = attr;
        const dk = autosave.key();
        if (dk) body.draft_key = dk;
        const r = await api.post("/demo/request", body);
        setFieldErrors({});
        t = r.data.request_token;
        setToken(t);
        fa.submitted();
        autosave.finish();
      }
      await onBook(t, slot, replace);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const fe = !t && fieldErrorsFrom(err);
      if (fe) {
        setFieldErrors(fe);
        const n = Object.keys(fe).length;
        setError(n === 1 ? "Please fix the highlighted field above." : `Please fix the ${n} highlighted fields above.`);
        const first = FIELD_ORDER.find((f) => fe[f]);
        document.querySelector(`[data-testid="demo-field-${first}"]`)?.focus();
      } else if (d?.error === "ALREADY_BOOKED" && d.booking) { setExisting(d.booking); setConfirmOpen(true); }
      else setError(errText(err) + (t ? " Your details are saved — please choose another time above." : ""));
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  if (!cfg.enabled) return <p className="text-sm text-slate-600" data-testid="demo-unavailable">Online demo scheduling isn't available right now. Please email sales@cyannect.com.</p>;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="demo-request-form" {...fa.formProps}>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([n, l, t, r, p]) => <Field key={n} name={n} label={l} type={t} required={r} placeholder={p} value={form[n] || ""} onChange={set} error={fieldErrors[n]} />)}
        <label className="block text-sm">
          <span className="font-medium text-slate-800">Phone (optional)</span>
          <input type="tel" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+1 416 555 1234"
            aria-describedby={fieldErrors.phone ? "demo-error-phone demo-phone-help" : "demo-phone-help"} aria-invalid={!!fieldErrors.phone}
            data-testid="demo-field-phone" className={inputCls(fieldErrors.phone)} />
          <FieldError name="phone" error={fieldErrors.phone} />
          <span id="demo-phone-help" className="text-xs text-slate-500">Canadian phone numbers only, if you add one</span>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-800">Company size (optional)</span>
          <select value={form.company_size || ""} onChange={(e) => set("company_size", e.target.value)} data-testid="demo-field-company_size"
            aria-invalid={!!fieldErrors.company_size} className={inputCls(fieldErrors.company_size)}>
            <option value="">Select</option>{cfg.company_sizes.map((s) => <option key={s} value={s}>{s} employees</option>)}
          </select>
          <FieldError name="company_size" error={fieldErrors.company_size} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-800">Province (optional)</span>
          <select value={form.province || ""} onChange={(e) => set("province", e.target.value)} data-testid="demo-field-province"
            aria-invalid={!!fieldErrors.province} className={inputCls(fieldErrors.province)}>
            <option value="">Select</option>{cfg.provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <FieldError name="province" error={fieldErrors.province} />
        </label>
      </div>
      <fieldset>
        <legend className="text-sm font-medium text-slate-800">What would you like to see?</legend>
        <div className="flex flex-wrap gap-2 mt-2">
          {cfg.interests.map((i) => {
            const on = form.demo_interests.includes(i);
            return (
              <button type="button" key={i} onClick={() => toggle(i)} aria-pressed={on} data-testid={`demo-interest-${i.replace(/\W+/g, "-").toLowerCase()}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-900"}`}>{i}</button>
            );
          })}
        </div>
        <FieldError name="demo_interests" error={fieldErrors.demo_interests} />
      </fieldset>
      <label className="block text-sm">
        <span className="font-medium text-slate-800">Anything else? (optional)</span>
        <textarea rows={3} maxLength={2000} value={form.message || ""} onChange={(e) => set("message", e.target.value)} data-testid="demo-field-message"
          aria-invalid={!!fieldErrors.message} className={inputCls(fieldErrors.message)} />
        <FieldError name="message" error={fieldErrors.message} />
      </label>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website_hp || ""} onChange={(e) => set("website_hp", e.target.value)} />
      <p className="text-sm text-slate-700" data-testid="demo-selected-slot">
        {slot ? <>Selected time: <strong>{fmtDay(slot.local_date)} · {slot.local_time} {slot.tz_abbrev}</strong> (20 minutes)</>
          : <span className="text-slate-500">No time selected yet — choose one above.</span>}
      </p>
      {existing && <ExistingDemoNotice existing={existing} kept={kept} />}
      {error && <p role="alert" className="text-sm text-rose-600" data-testid="demo-form-error">{error}</p>}
      <button type="submit" disabled={busy || !slot} data-testid="demo-form-submit" className="pf-btn-primary px-6 py-3 rounded-full disabled:opacity-50">
        {busy ? "Booking your demo…" : !slot ? "Choose a time above to continue"
          : `${existing ? "Change my demo to" : "Book my demo ·"} ${slot.local_time} ${slot.tz_abbrev}`}
      </button>
      <MoveDemoDialog open={confirmOpen} existing={existing} slot={slot}
        onKeep={() => { setConfirmOpen(false); setKept(true); }} onMove={() => run(true)} />
      <p className="text-xs text-slate-500">We use these details only to prepare your demo. Booking a demo doesn't sign you up for marketing.</p>
    </form>
  );
}
